import { useCallback, useState } from "react";
import { useEditor } from "@craftjs/core";
import { apiClient } from "@api/index";
import { LoadingButton } from "@mui/lab";
import toast from "react-hot-toast";
import { Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useAuthStore } from "@state/auth";
import { useEditorStore } from "@state/editor";
import { useThemeStore } from "@state/theme";
import { Link, useNavigate } from "react-router-dom";
import { AppType, AppStatus } from "@utils/constants";
import { formatNodes } from "@utils/format";
import { useConfigStore } from "@state/config";
import { hasPermission, hasAnyPermission } from "@utils/permissions";

interface TopbarProps {
  appStatus: AppStatus;
  appName: string;
  appTypes: AppType[];
  appID: string;
  toggleComponentsPanelOpen: () => void;
  toggleSettingsPanelOpen: () => void;
}

const Topbar = ({ appID, appName, appTypes, appStatus, toggleComponentsPanelOpen, toggleSettingsPanelOpen }: TopbarProps) => {
  const { query } = useEditor();
  const navigate = useNavigate();
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const { mutateAsync: updateApp, isPending: updateAppIsLoading } = apiClient.ott.updateApp.useMutation({
    onSuccess: () => {
      // invalidateQueries().then(() => {
      toast.success("App Successfully Updated");
      // });
    },
    onError: (err) => {
      toast.error(err as string);
    },
  });

  const user = useAuthStore((state) => state.user);
  const theme = useThemeStore();
  const config = useConfigStore();

  const { setIsPreview, isPreview } = useEditorStore();

  const canUpdate = hasPermission(user.roles, "manage_ott", "update");
  const canAccessSettings = hasAnyPermission(user.roles, "manage_ott", ["create", "delete"]);

  const handleUpdateApp = () => {
    const appNameTrimmed = appName.trim();

    updateApp({
      appID,
      appName: appNameTrimmed,
      targets: appTypes,
      clientID: user.clientID,
      manifest: {
        config: {
          allowVAST: config.allowVAST,
          thumbMissingUrl: config.thumbMissingUrl,
        },
        elements: formatNodes(query.getSerializedNodes()),
        status: appStatus,
        theme: theme,
      },
    });
  };

  const handlePreviewToggle = useCallback(() => {
    setIsPreview(!isPreview);
  }, [setIsPreview, isPreview]);

  const handleSettingsClick = () => {
    setShowUnsavedWarning(true);
  };

  const confirmNavigateToSettings = () => {
    setShowUnsavedWarning(false);
    navigate(`/app/settings/${appID}`);
  };

  return (
    <nav className="flex items-center justify-between flex-wrap p-5 bg-white">
      <div className="flex items-center flex-shrink-0 text-black mr-6 gap-3">
        {isPreview ? (
          <button onClick={handlePreviewToggle}>
            <Tooltip
              title="Editor"
              placement="top"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>
            </Tooltip>
          </button>
        ) : (
          <Link to="/">
            <Tooltip
              title="Go home"
              placement="top"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>
            </Tooltip>
          </Link>
        )}

        {!isPreview && (
          <div className="flex gap-3">
            {/* Toggle Left Panel Visibility*/}

            <Tooltip
              title="Toggle component panel visibility"
              placement="top"
            >
              <button onClick={toggleComponentsPanelOpen}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  className="w-6 h-6"
                >
                  <rect
                    width="18"
                    height="18"
                    x="3"
                    y="3"
                    rx="2"
                    ry="2"
                  ></rect>
                  <line
                    x1="9"
                    x2="9"
                    y1="3"
                    y2="21"
                  ></line>
                </svg>
              </button>
            </Tooltip>

            {/* Toggle Right Panel Visibility */}
            <Tooltip
              title="Toggle settings panel visibility"
              placement="top"
            >
              <button onClick={toggleSettingsPanelOpen}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  className="w-6 h-6"
                >
                  <rect
                    width="18"
                    height="18"
                    x="3"
                    y="3"
                    rx="2"
                    ry="2"
                  ></rect>
                  <line
                    x1="15"
                    x2="15"
                    y1="3"
                    y2="21"
                  ></line>
                </svg>
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        {/* <Button
          size="medium"
          variant="outlined"
          color="secondary"
          onClick={handlePreviewToggle}
        >
          {isPreview ? "Edit" : "Preview Page"}
        </Button> */}

        {/* {!isPreview && (
          <LoadingButton
            size="medium"
            variant="contained"
            color="primary"
            loading={updateAppIsLoading}
            onClick={handleUpdateApp}
          >
            Save
          </LoadingButton>
        )}

        {!isPreview && (
          <LoadingButton
            size="medium"
            variant="contained"
            color="primary"
            onClick={() => navigate(`/app/settings/${appID}`)}
          >
            Publish
          </LoadingButton>
        )} */}

        {!isPreview && (
          <Button
            size="medium"
            variant="outlined"
            color="primary"
            component="a"
            href="https://invintus-support.freshdesk.com/support/solutions/folders/48000701827"
            target="_blank"
            rel="noopener noreferrer"
          >
            Documentation
          </Button>
        )}

        {!isPreview && (
          <Tooltip
            title={!canUpdate ? "You do not have permission to update apps" : ""}
            placement="bottom"
          >
            <span>
              <LoadingButton
                size="medium"
                variant="contained"
                color="primary"
                loading={updateAppIsLoading}
                onClick={handleUpdateApp}
                disabled={!canUpdate}
              >
                Publish
              </LoadingButton>
            </span>
          </Tooltip>
        )}

        {!isPreview && (
          <Tooltip
            title={!canAccessSettings ? "You do not have permission to access settings" : ""}
            placement="bottom"
          >
            <span>
              <LoadingButton
                size="medium"
                variant="contained"
                color="primary"
                onClick={handleSettingsClick}
                disabled={!canAccessSettings}
              >
                Settings
              </LoadingButton>
            </span>
          </Tooltip>
        )}
      </div>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog
        open={showUnsavedWarning}
        onClose={() => setShowUnsavedWarning(false)}
        sx={{
          zIndex: 9999,
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            color: "black",
          }}
        >
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: "gray",
            }}
          >
            Any unsaved changes will not be saved. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions className="gap-2">
          <Button
            onClick={() => setShowUnsavedWarning(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmNavigateToSettings}
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </nav>
  );
};

export default Topbar;
